import { useEffect, useRef, useState } from "react";
import type { AssistantMode, AuthUser, HealthInfo, OrgMember, SessionSort, SessionSummary, TagCount, WorkspaceSummary } from "../types";
import { isPrivileged } from "../types";
import type { Theme } from "../hooks/useTheme";
import { ModeSelector } from "./ModeSelector";
import { SkeletonList } from "./Skeleton";
import { NotificationBell } from "./NotificationBell";
import { SearchFiltersPopover, type SourceFilter } from "./SearchFiltersPopover";
import { TagFilterPopover } from "./TagFilterPopover";
import { ConfirmModal } from "./ConfirmModal";

interface Props {
  sessions: SessionSummary[];
  sessionsLoading: boolean;
  sessionsLoadingMore: boolean;
  sessionsSentinelRef: (node: HTMLDivElement | null) => void;
  activeId: string;
  health: HealthInfo | null;
  workspaces: WorkspaceSummary[];
  activeWorkspaceId: string | null;
  onSwitchWorkspace: (id: string) => void;
  searchQuery: string;
  tagFilter: string;
  onTagFilterChange: (tag: string) => void;
  availableTags: TagCount[];
  onUpdateTags: (id: string, tags: string[]) => void;
  /** Exclui a tag de todas as conversas do próprio usuário (com confirmação). */
  onDeleteTag: (tag: string) => void;
  assignedFilter: string;
  onAssignedFilterChange: (v: string) => void;
  orgMembers: OrgMember[];
  onAssign: (id: string, userId: string | null) => void;
  currentUserId: string;
  pinnedOnly: boolean;
  onPinnedOnlyChange: (v: boolean) => void;
  dateFrom: string;
  onDateFromChange: (v: string) => void;
  dateTo: string;
  onDateToChange: (v: string) => void;
  sessionSort: SessionSort;
  onSessionSortChange: (v: SessionSort) => void;
  sourceFilter: SourceFilter;
  onSourceFilterChange: (v: SourceFilter) => void;
  theme: Theme;
  onCycleTheme: () => void;
  globalMode: AssistantMode;
  onChangeGlobalMode: (m: AssistantMode) => void;
  user: AuthUser;
  onLogout: () => void;
  onSearchChange: (q: string) => void;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onTogglePin: (id: string, pinned: boolean) => void;
  onOpenAbout: () => void;
  onOpenStats: () => void;
  onOpenAdmin: () => void;
  onOpenMyData: () => void;
  aboutActive: boolean;
  statsActive: boolean;
  adminActive: boolean;
  myDataActive: boolean;
  /** No mobile a sidebar é um drawer; no desktop a prop é ignorada pelo CSS. */
  open?: boolean;
  onOpenNotificationLink?: (link: string) => void;
  onImported: () => void;
}

/**
 * Menu de conta (épico 6-10) - Estatísticas, Exportar, Meus dados, Admin,
 * Repos e Sobre foram se empilhando soltos no rodapé, um link novo por
 * épico, até sobrar pouco espaço pra lista de conversas. Consolidados aqui,
 * atrás do próprio perfil do usuário - mesmo padrão de ChatGPT/Notion/Linear
 * (clicar no perfil no rodapé abre o menu de conta, com "Sair" por último).
 */
function AccountMenu({
  user,
  onLogout,
  children,
}: {
  user: AuthUser;
  onLogout: () => void;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div className="account-menu-wrap" ref={ref}>
      {open && (
        <div className="account-menu" onClick={() => setOpen(false)}>
          {children}
          <div className="account-menu-divider" />
          <button className="account-menu-item logout" onClick={onLogout} type="button">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M6 14H3.5A1.5 1.5 0 0 1 2 12.5v-9A1.5 1.5 0 0 1 3.5 2H6" />
              <path d="M10.5 11L14 8l-3.5-3M14 8H6" />
            </svg>
            Sair
          </button>
        </div>
      )}
      <button className="user-box" onClick={() => setOpen((v) => !v)} type="button" aria-expanded={open} data-tour="account">
        <span
          className="user-avatar"
          aria-hidden
          style={
            user.avatarDataUrl
              ? { backgroundImage: `url(${user.avatarDataUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
              : undefined
          }
        >
          {!user.avatarDataUrl && (user.name || user.email).charAt(0).toUpperCase()}
        </span>
        <span className="user-box-text">
          <span className="user-box-name">
            {user.name}
            {user.role !== "user" && (
              <span className="badge-admin">{user.role === "sysadmin" ? "sysadmin" : "admin"}</span>
            )}
          </span>
          <span className="user-box-email">{user.email}</span>
        </span>
        <svg className="user-box-chevron" width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d={open ? "M4 10l4-4 4 4" : "M4 6l4 4 4-4"} />
        </svg>
      </button>
    </div>
  );
}

export function Sidebar({
  open = false,
  sessions,
  sessionsLoading,
  sessionsLoadingMore,
  sessionsSentinelRef,
  activeId,
  health,
  workspaces,
  activeWorkspaceId,
  onSwitchWorkspace,
  searchQuery,
  tagFilter,
  onTagFilterChange,
  availableTags,
  onUpdateTags,
  onDeleteTag,
  assignedFilter,
  onAssignedFilterChange,
  orgMembers,
  onAssign,
  currentUserId,
  pinnedOnly,
  onPinnedOnlyChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  sessionSort,
  onSessionSortChange,
  sourceFilter,
  onSourceFilterChange,
  theme,
  onCycleTheme,
  globalMode,
  onChangeGlobalMode,
  user,
  onLogout,
  onSearchChange,
  onSelect,
  onNew,
  onDelete,
  onRename,
  onTogglePin,
  onOpenAbout,
  onOpenStats,
  onOpenAdmin,
  onOpenMyData,
  aboutActive,
  statsActive,
  adminActive,
  myDataActive,
  onOpenNotificationLink,
  onImported,
}: Props) {
  const importInputRef = useRef<HTMLInputElement>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);

  const pickImportFile = () => importInputRef.current?.click();

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite escolher o mesmo arquivo de novo depois
    if (!file) return;
    setImportBusy(true);
    setImportMsg(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const r = await fetch("/api/sessions/import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(parsed),
      });
      const j = await r.json().catch(() => ({}) as { error?: string });
      if (!r.ok) {
        setImportMsg(j.error ?? "falha ao importar o backup");
        return;
      }
      setImportMsg(`${j.imported} conversa(s) importada(s)${j.skipped ? `, ${j.skipped} ignorada(s)` : ""}.`);
      onImported();
    } catch {
      setImportMsg("arquivo inválido - escolha um .json exportado deste app");
    } finally {
      setImportBusy(false);
      setTimeout(() => setImportMsg(null), 6000);
    }
  };
  return (
    <aside className={open ? "sidebar is-open" : "sidebar"}>
      <div className="sidebar-header">
        <div className="brand">
          {health?.logoDataUrl ? (
            <img src={health.logoDataUrl} alt={health.appName} className="brand-logo" />
          ) : (
            <span className="brand-dot" />
          )}
          <span className="brand-name">{health?.appName ?? "Assistant"}</span>
          <NotificationBell onOpenLink={onOpenNotificationLink} />
          <ThemeToggle theme={theme} onCycle={onCycleTheme} />
        </div>
        {workspaces.length > 1 && (
          <label className="workspace-select" title="Workspace ativo (escopo do código consultado)">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M2 4.5h5l1.5 1.5H14v6.5a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4.5z" />
            </svg>
            <select
              value={activeWorkspaceId ?? ""}
              onChange={(e) => onSwitchWorkspace(e.target.value)}
              aria-label="Trocar de workspace"
            >
              {workspaces.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <button className="new-chat-btn" onClick={onNew} data-tour="new-chat">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <path d="M8 3v10M3 8h10" />
          </svg>
          Nova conversa
        </button>

        <div className="search-row">
          <div className="search-box">
            <svg className="search-icon" width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
              <circle cx="7" cy="7" r="4.5" />
              <path d="M13 13l-2.5-2.5" />
            </svg>
            <input
              type="search"
              data-search-input
              placeholder="Buscar nas conversas…"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              spellCheck={false}
            />
            {searchQuery && (
              <button
                type="button"
                className="search-clear"
                onClick={() => onSearchChange("")}
                aria-label="Limpar busca"
                title="Limpar"
              >
                ×
              </button>
            )}
          </div>
          <SearchFiltersPopover
            pinnedOnly={pinnedOnly}
            onPinnedOnlyChange={onPinnedOnlyChange}
            dateFrom={dateFrom}
            onDateFromChange={onDateFromChange}
            dateTo={dateTo}
            onDateToChange={onDateToChange}
            sessionSort={sessionSort}
            onSessionSortChange={onSessionSortChange}
            sourceFilter={sourceFilter}
            onSourceFilterChange={onSourceFilterChange}
          />
        </div>

        {(availableTags.length > 0 || orgMembers.length > 1) && (
          <div className="tag-filter-bar">
            {orgMembers.length > 1 && (
              <button
                type="button"
                className={`tag-chip ${assignedFilter === "me" ? "active" : ""}`}
                onClick={() => onAssignedFilterChange(assignedFilter === "me" ? "" : "me")}
                title="Conversas atribuídas a mim"
              >
                Atribuídas a mim
              </button>
            )}
            <TagFilterPopover tags={availableTags} value={tagFilter} onChange={onTagFilterChange} onDelete={onDeleteTag} />
          </div>
        )}
      </div>

      <nav className="sessions">
        {sessionsLoading ? (
          <div className="sessions-skeleton">
            <SkeletonList rows={8} />
          </div>
        ) : sessions.length === 0 ? (
          <div className="sessions-empty">
            {searchQuery
              ? "Nada encontrado para esta busca."
              : tagFilter
                ? `Nenhuma conversa com a tag "${tagFilter}".`
                : assignedFilter === "me"
                  ? "Nenhuma conversa atribuída a você."
                  : "Sem conversas ainda."}
          </div>
        ) : (
          <>
            {sessions.map((s) => (
              <SessionItem
                key={s.id}
                session={s}
                active={!aboutActive && !statsActive && s.id === activeId}
                onSelect={() => onSelect(s.id)}
                onDelete={() => onDelete(s.id)}
                onRename={(title) => onRename(s.id, title)}
                onTogglePin={() => onTogglePin(s.id, !s.pinned)}
                onUpdateTags={(tags) => onUpdateTags(s.id, tags)}
                orgMembers={orgMembers}
                currentUserId={currentUserId}
                onAssign={(userId) => onAssign(s.id, userId)}
              />
            ))}
            <div ref={sessionsSentinelRef} className="infinite-sentinel" aria-hidden />
            {sessionsLoadingMore && (
              <div className="sessions-skeleton">
                <SkeletonList rows={2} />
              </div>
            )}
          </>
        )}
      </nav>

      <div className="sidebar-footer">
        <div className="global-mode" title="Modo de resposta padrão de novas conversas">
          <span className="global-mode-label">Modo padrão</span>
          <ModeSelector
            value={globalMode}
            onChange={(m) => onChangeGlobalMode((m || user.allowedModes[0] || "business") as AssistantMode)}
            allowed={user.allowedModes}
          />
        </div>
        <AccountMenu user={user} onLogout={onLogout}>
          <button
            className={`account-menu-item ${statsActive ? "active" : ""}`}
            onClick={onOpenStats}
            type="button"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M2 14V2M2 14h12M5 11V7M8.5 11V4M12 11V8.5" />
            </svg>
            Estatísticas
          </button>
          <a className="account-menu-item" href="/api/export" download>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M8 2v9M4.5 7.5L8 11l3.5-3.5" />
              <path d="M3 13h10" />
            </svg>
            Exportar backup (.json)
          </a>
          <button className="account-menu-item" onClick={pickImportFile} type="button" disabled={importBusy}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M8 11V2M4.5 5.5L8 2l3.5 3.5" />
              <path d="M3 13h10" />
            </svg>
            {importBusy ? "Importando…" : "Importar backup (.json)"}
          </button>
          <button
            className={`account-menu-item ${myDataActive ? "active" : ""}`}
            onClick={onOpenMyData}
            type="button"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect x="2.5" y="3" width="11" height="10" rx="1.5" />
              <path d="M5.5 6.5h5M5.5 9.5h3" />
            </svg>
            Meus dados (LGPD)
          </button>
          {isPrivileged(user.role) && (
            <button
              className={`account-menu-item ${adminActive ? "active" : ""}`}
              onClick={onOpenAdmin}
              type="button"
              data-tour="admin"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M8 1.5l5.5 2.5v4c0 3.2-2.3 5.6-5.5 6.5C4.8 13.6 2.5 11.2 2.5 8V4L8 1.5z" />
                <path d="M6 8l1.5 1.5L10.5 6.5" />
              </svg>
              Administração
            </button>
          )}
          {/* "Repositórios" saiu do menu: a página vive em Administração ->
              Repositórios (a rota /repos segue como atalho direto). */}
          <button
            className={`account-menu-item ${aboutActive ? "active" : ""}`}
            onClick={onOpenAbout}
            type="button"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="8" cy="8" r="6.5" />
              <path d="M8 7.5v3.5M8 5h.01" />
            </svg>
            Sobre o assistente
          </button>
        </AccountMenu>
        <input
          ref={importInputRef}
          type="file"
          accept="application/json"
          style={{ display: "none" }}
          onChange={(e) => void handleImportFile(e)}
        />
        {importMsg && <div className="import-toast">{importMsg}</div>}
        {/* Detalhe de infra (modo de auth do motor + modelo): só interessa ao
            operador da plataforma - cliente final não precisa ver "API key /
            default (CLI)" no rodapé. */}
        {user.role === "sysadmin" &&
          (health ? (
            <div className="footer-info">
              <span className={`badge ${health.authMode === "oauth" ? "ok" : "warn"}`}>
                {health.authMode === "oauth" ? "OAuth" : "API key"}
              </span>
              <span className="footer-model" title={health.model}>
                {health.model}
              </span>
            </div>
          ) : (
            <div className="footer-info dim">conectando…</div>
          ))}
      </div>
    </aside>
  );
}

function ThemeToggle({ theme, onCycle }: { theme: Theme; onCycle: () => void }) {
  const label =
    theme === "light" ? "Tema: claro" : theme === "dark" ? "Tema: escuro" : "Tema: sistema";
  return (
    <button className="theme-toggle" onClick={onCycle} title={`${label} (clique para alternar)`} aria-label={label} type="button">
      {theme === "light" ? (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
          <circle cx="8" cy="8" r="3" />
          <path d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M3 3l1 1M12 12l1 1M13 3l-1 1M4 12l-1 1" />
        </svg>
      ) : theme === "dark" ? (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
          <path d="M13 9.5A5.5 5.5 0 0 1 6.5 3a5.5 5.5 0 1 0 6.5 6.5z" />
        </svg>
      ) : (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="1.5" y="3" width="13" height="8.5" rx="1.5" />
          <path d="M5.5 14h5M8 11.5V14" />
        </svg>
      )}
    </button>
  );
}

function SessionItem({
  session,
  active,
  onSelect,
  onDelete,
  onRename,
  onTogglePin,
  onUpdateTags,
  orgMembers,
  currentUserId,
  onAssign,
}: {
  session: SessionSummary;
  active: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onRename: (title: string) => void;
  onTogglePin: () => void;
  onUpdateTags: (tags: string[]) => void;
  orgMembers: OrgMember[];
  currentUserId: string;
  onAssign: (userId: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(session.title);
  const inputRef = useRef<HTMLInputElement>(null);

  const [editingTags, setEditingTags] = useState(false);
  const [tagDraft, setTagDraft] = useState("");
  const tagInputRef = useRef<HTMLInputElement>(null);

  const [assigning, setAssigning] = useState(false);
  const assignRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!assigning) return;
    const onClick = (e: MouseEvent) => {
      if (assignRef.current && !assignRef.current.contains(e.target as Node)) setAssigning(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [assigning]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    if (editingTags && tagInputRef.current) {
      tagInputRef.current.focus();
      tagInputRef.current.select();
    }
  }, [editingTags]);

  const startEditTags = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTagDraft((session.tags ?? []).join(", "));
    setEditingTags(true);
  };

  const commitTags = () => {
    setEditingTags(false);
    const parsed = tagDraft
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 10);
    onUpdateTags(parsed);
  };

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraft(session.title);
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== session.title) onRename(next);
  };

  // confirmação customizada (regra do projeto: nenhum diálogo nativo)
  const [confirmDelete, setConfirmDelete] = useState(false);
  const onDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDelete(true);
  };

  const onPinClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTogglePin();
  };

  const tokenCount = session.totalTokens ?? 0;

  if (editing) {
    return (
      <div className={`session-item editing ${active ? "active" : ""}`}>
        <input
          ref={inputRef}
          className="session-rename-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              setEditing(false);
            }
          }}
          spellCheck={false}
        />
      </div>
    );
  }

  if (editingTags) {
    return (
      <div className={`session-item editing ${active ? "active" : ""}`}>
        <input
          ref={tagInputRef}
          className="session-rename-input"
          value={tagDraft}
          onChange={(e) => setTagDraft(e.target.value)}
          onBlur={commitTags}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitTags();
            } else if (e.key === "Escape") {
              setEditingTags(false);
            }
          }}
          placeholder="tags separadas por vírgula…"
          spellCheck={false}
        />
      </div>
    );
  }

  return (
    <>
    <button
      className={`session-item ${active ? "active" : ""} ${session.pinned ? "pinned" : ""}`}
      onClick={onSelect}
      title={session.title}
    >
      <div className="session-line">
        {session.pinned && <PinDot />}
        <span className="session-title">{session.title}</span>
        {session.source === "api" && (
          <span className="session-source-badge" title="Conversa criada via API">
            API
          </span>
        )}
        <span className="session-tools">
          <span
            className="session-tool"
            onClick={onPinClick}
            title={session.pinned ? "Desafixar" : "Fixar no topo"}
            role="button"
            tabIndex={0}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill={session.pinned ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9.5 2l4.5 4.5-2 .5-2.5 2.5-.5 3-3-3L2 13M9.5 2 7 4.5l-3 .5 7 7 .5-3L14 6.5" />
            </svg>
          </span>
          <span
            className="session-tool"
            onClick={startEdit}
            title="Renomear conversa"
            role="button"
            tabIndex={0}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 2.5l2.5 2.5M3 13l8.5-8.5 2.5 2.5L5.5 15.5 2.5 16l.5-3z" />
            </svg>
          </span>
          <span
            className="session-tool"
            onClick={startEditTags}
            title="Editar tags"
            role="button"
            tabIndex={0}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8.5 2h4a1 1 0 0 1 1 1v4l-6.5 6.5a1 1 0 0 1-1.4 0L2.5 9.4a1 1 0 0 1 0-1.4L9 2z" />
              <circle cx="10.5" cy="5.5" r="0.9" fill="currentColor" stroke="none" />
            </svg>
          </span>
          {orgMembers.length > 1 && (
            <div className="session-assign-wrap" ref={assignRef}>
              <span
                className={`session-tool ${session.assignedTo ? "assigned" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setAssigning((v) => !v);
                }}
                title={session.assignedToEmail ? `Atribuída a ${session.assignedToEmail}` : "Atribuir a um colega"}
                role="button"
                tabIndex={0}
              >
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="8" cy="5.5" r="2.5" />
                  <path d="M3 14c0-2.8 2.2-4.5 5-4.5s5 1.7 5 4.5" />
                </svg>
              </span>
              {assigning && (
                <div className="session-assign-menu" onClick={(e) => e.stopPropagation()}>
                  <div
                    className={`session-assign-item ${!session.assignedTo ? "active" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      onAssign(null);
                      setAssigning(false);
                    }}
                  >
                    Ninguém
                  </div>
                  {orgMembers.map((m) => (
                    <div
                      key={m.id}
                      className={`session-assign-item ${session.assignedTo === m.id ? "active" : ""}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        onAssign(m.id);
                        setAssigning(false);
                      }}
                    >
                      {m.id === currentUserId ? `${m.name} (você)` : m.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <span
            className="session-tool"
            onClick={onDeleteClick}
            title="Apagar conversa"
            role="button"
            tabIndex={0}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
              <path d="M3 4h10M6.5 4V3a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1M5 4l.5 9a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1l.5-9" />
            </svg>
          </span>
        </span>
      </div>
      {session.assignedToEmail && (
        <div className="session-assigned-badge" title={`Atribuída a ${session.assignedToEmail}`}>
          → {session.assignedToEmail}
        </div>
      )}
      {session.tags && session.tags.length > 0 && (
        <div className="session-tags">
          {session.tags.map((t) => (
            <span key={t} className="session-tag-chip">
              {t}
            </span>
          ))}
        </div>
      )}
      {session.matchSnippet && (
        <div className="session-snippet" title={session.matchSnippet}>
          {session.matchSnippet}
        </div>
      )}
      {tokenCount > 0 && (
        <div className="session-meta">
          {formatTokens(tokenCount)} tokens
          {session.totalCostUsd != null && session.totalCostUsd > 0
            ? ` • $${session.totalCostUsd.toFixed(3)}`
            : ""}
        </div>
      )}
    </button>
    {confirmDelete && (
      <ConfirmModal
        title="Apagar conversa"
        message={`Apagar "${session.title}"? A conversa sai da sua lista.`}
        confirmLabel="Apagar"
        onConfirm={onDelete}
        onClose={() => setConfirmDelete(false)}
      />
    )}
    </>
  );
}

function PinDot() {
  return (
    <svg className="pin-dot" width="11" height="11" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path d="M9.5 2l4.5 4.5-2 .5-2.5 2.5-.5 3-3-3L2 13l4.5-4.5-3-3 3-.5L9.5 2z" />
    </svg>
  );
}

function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}
